/**
 * Players, their rounds, stats and sessions (GET/PATCH /api/players, GET /api/sessions,
 * /api/rounds-final, /api/rounds/player-stats).
 *
 * Traps handled here (source):
 * - the players list serialises CreatedAt/UpdatedAt as `{}` (the hex transform walks Dates): tolerated;
 * - PATCH takes `Status` and `IsTestPlayer` only, as 0/1;
 * - `/players/:id/rtp-analytics` mixes units: `configuredRtp` is the game's 0–1 fraction, the other
 *   RTPs are percentages. Both come out of here as 0–1 fractions;
 * - `/rounds/player-stats` compares `CreatedAt <= toDate`, so the end date is sent as 23:59:59;
 * - sessions take paging only (no filter or sort), `GET /sessions/:id` is by player id, and rows carry
 *   the session hash and wallet session state, which the schema drops.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { bigintString, flag } from '@/api/zod-helpers';
import type { FieldMeta } from '@/lib/diff';
import { majorToCents } from '@/lib/money-units';
import { normalizeRtp } from '@/lib/rtp';
import { filterParam, listSearchSchema, toPagedQuery, type ListSearch } from '@/lib/url-state';

const text = z
  .string()
  .nullish()
  .transform((v) => v ?? null);
const count = z.coerce
  .number()
  .nullish()
  .transform((v) => v ?? null);
/** An ISO string, or `{}` where the list serialises a Date badly. */
const timestamp = z.unknown().transform((v) => (typeof v === 'string' && v ? v : null));

// ---- players ----------------------------------------------------------------------------------

export const playerSchema = z.object({
  Id: z.coerce.number(),
  operatorId: count,
  OperatorPlayerId: text,
  OperatorPlayerNickname: text,
  Status: z.coerce.number(),
  Currency: text,
  Country: text,
  License: text,
  Brand: text,
  IsTestPlayer: flag,
  CreatedAt: timestamp,
  UpdatedAt: timestamp,
  Operator: z.object({ Name: text }).nullish(),
});

export type Player = z.infer<typeof playerSchema>;

export const playerName = (p: Pick<Player, 'OperatorPlayerNickname' | 'Id'>) =>
  p.OperatorPlayerNickname?.trim() || `Player ${p.Id}`;

export const PLAYER_SORT_FIELDS = [
  'Id',
  'operatorId',
  'OperatorPlayerId',
  'OperatorPlayerNickname',
  'Status',
  'Currency',
  'Country',
  'License',
  'Brand',
  'IsTestPlayer',
  'CreatedAt',
  'UpdatedAt',
] as const;

export const DEFAULT_PLAYER_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

export const playersSearchSchema = listSearchSchema({
  sortFields: PLAYER_SORT_FIELDS,
  filters: {
    operator: filterParam.number(),
    active: filterParam.boolean(),
    test: filterParam.boolean(),
    currency: filterParam.string(),
    brand: filterParam.string(),
  },
});

export type PlayersSearch = z.infer<typeof playersSearchSchema>;

/** Quick search: `#123` = our player id, digits = the operator's player id, else nickname. */
export function playersPagedQuery(search: PlayersSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  const q = search.q?.trim();
  if (q) {
    const own = /^#(\d+)$/.exec(q);
    if (own?.[1]) filters.push({ field: 'Id', operator: 'eq', value: Number(own[1]) });
    else if (/^\d+$/.test(q)) filters.push({ field: 'OperatorPlayerId', operator: 'eq', value: q });
    else filters.push({ field: 'OperatorPlayerNickname', operator: 'contains', value: q });
  }
  if (search.operator !== undefined) filters.push({ field: 'operatorId', operator: 'eq', value: search.operator });
  if (search.active !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.active ? 1 : 0 });
  if (search.test !== undefined) filters.push({ field: 'IsTestPlayer', operator: 'eq', value: search.test ? 1 : 0 });
  if (search.currency) filters.push({ field: 'Currency', operator: 'eq', value: search.currency });
  if (search.brand) filters.push({ field: 'Brand', operator: 'eq', value: search.brand });
  return toPagedQuery(search, { defaultSort: DEFAULT_PLAYER_SORT, filters });
}

export const playersKeys = {
  all: ['players'] as const,
  list: (query: PagedQuery) => ['players', 'list', query] as const,
  detail: (id: number) => ['players', 'detail', id] as const,
  rounds: (id: number, query: PagedQuery) => ['players', 'rounds', id, query] as const,
  games: (id: number) => ['players', 'games', id] as const,
  stats: (id: number, from: string, to: string) => ['players', 'stats', id, from, to] as const,
  rtp: (id: number, gameId: number, from: string, to: string) => ['players', 'rtp', id, gameId, from, to] as const,
  session: (id: number) => ['players', 'session', id] as const,
  sessions: (query: PagedQuery) => ['players', 'sessions', query] as const,
  operators: ['players', 'lookup', 'operators'] as const,
};

const playersListSchema = pagedSchema(playerSchema);

export const playersListQuery = (search: PlayersSearch) => {
  const query = playersPagedQuery(search);
  return queryOptions({
    queryKey: playersKeys.list(query),
    queryFn: ({ signal }) => http('/players', { query: encodePagedQuery(query), signal }, playersListSchema),
    placeholderData: keepPreviousData,
  });
};

export const playerQuery = (id: number) =>
  queryOptions({
    queryKey: playersKeys.detail(id),
    queryFn: ({ signal }) => http(`/players/${id}`, { signal }, playerSchema),
  });

export interface PlayerSettings extends Record<string, unknown> {
  Status: boolean;
  IsTestPlayer: boolean;
}

export const PLAYER_SETTINGS_FIELDS: FieldMeta[] = [
  { key: 'Status', label: 'Status', format: (v) => (v ? 'Active' : 'Inactive') },
  { key: 'IsTestPlayer', label: 'Test player' },
];

export function useUpdatePlayer(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PlayerSettings>) => {
      const body: Record<string, number> = {};
      if (patch.Status !== undefined) body.Status = patch.Status ? 1 : 0;
      if (patch.IsTestPlayer !== undefined) body.IsTestPlayer = patch.IsTestPlayer ? 1 : 0;
      return http(`/players/${id}`, { method: 'PATCH', body }, playerSchema);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: playersKeys.all });
    },
  });
}

// ---- operators (filter options) ---------------------------------------------------------------

const operatorOptionsSchema = pagedSchema(z.object({ Id: z.coerce.number(), Name: z.string() }));

export const operatorOptionsQuery = () =>
  queryOptions({
    queryKey: playersKeys.operators,
    queryFn: ({ signal }) =>
      http(
        '/operators',
        { query: { skip: 0, take: 1000, sort: JSON.stringify({ field: 'Name', dir: 'asc' }) }, signal },
        operatorOptionsSchema,
      ),
    staleTime: 5 * 60_000,
  });

// ---- the player's rounds (settled) ------------------------------------------------------------

/** Amounts arrive in major units; kept as cents for MoneyCell. */
const money = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((v) => majorToCents(v ?? null));

export const playerRoundSchema = z.object({
  Id: bigintString,
  gameId: z.coerce.number(),
  BetAmount: money,
  WinAmount: money,
  BonusWinAmount: money,
  JackpotWinAmount: money,
  Status: z.coerce.number(),
  Currency: z.string(),
  IsFreeSpin: flag,
  CreatedAt: timestamp,
  Game: z.object({ Name: text }).nullish(),
});

export type PlayerRound = z.infer<typeof playerRoundSchema>;

const playerRoundsSchema = pagedSchema(playerRoundSchema);

const newestFirst: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

/** Sortable like the rounds list (same allow-list; a sort on `Game.Name` is a 400). */
export const PLAYER_ROUND_SORT_FIELDS = [
  'Id',
  'Status',
  'WinAmount',
  'BetAmount',
  'BonusWinAmount',
  'CreatedAt',
] as const;
export const DEFAULT_PLAYER_ROUND_SORT = newestFirst;

export const playerRoundsQuery = (playerId: number, search: ListSearch) => {
  const query = toPagedQuery(search, {
    defaultSort: newestFirst,
    filters: [{ field: 'playerId', operator: 'eq', value: playerId }],
  });
  return queryOptions({
    queryKey: playersKeys.rounds(playerId, query),
    queryFn: ({ signal }) => http('/rounds-final', { query: encodePagedQuery(query), signal }, playerRoundsSchema),
    placeholderData: keepPreviousData,
  });
};

/** Games the player played recently (from their latest settled rounds), for the RTP picker. */
export const playerGamesQuery = (playerId: number) => {
  const query: PagedQuery = {
    skip: 0,
    take: 200,
    sort: newestFirst,
    filters: [{ field: 'playerId', operator: 'eq', value: playerId }],
  };
  return queryOptions({
    queryKey: playersKeys.games(playerId),
    queryFn: async ({ signal }) => {
      const page = await http('/rounds-final', { query: encodePagedQuery(query), signal }, playerRoundsSchema);
      const games = new Map<number, string>();
      for (const round of page.data)
        if (!games.has(round.gameId)) games.set(round.gameId, round.Game?.Name ?? `Game ${round.gameId}`);
      return [...games].map(([id, name]) => ({ id, name }));
    },
    staleTime: 60_000,
  });
};

// ---- stats per currency (rounds_final) --------------------------------------------------------

const statsRowSchema = z.object({
  Currency: z.string(),
  Bet: money,
  Win: money,
  RTP: z
    .union([z.string(), z.number()])
    .nullish()
    .transform((v) => normalizeRtpFraction(v)),
  Rounds: z.coerce.number(),
});

export type PlayerStatsRow = z.infer<typeof statsRowSchema>;

/** Actual RTP can be anything for a small sample, so no plausibility check here: just a number. */
function normalizeRtpFraction(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : null;
}

export const playerStatsQuery = (playerId: number, from: string, to: string) =>
  queryOptions({
    queryKey: playersKeys.stats(playerId, from, to),
    queryFn: ({ signal }) =>
      http(
        '/rounds/player-stats',
        { query: { playerId, fromDate: from, toDate: `${to} 23:59:59` }, signal },
        z.array(statsRowSchema),
      ),
  });

// ---- RTP per game (BigQuery) ------------------------------------------------------------------

const pct = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n / 100 : null;
  });

const amount = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((v) => (Number.isFinite(Number(v)) ? Number(v) : null));

const rtpSchema = z.object({
  overall: z.object({
    totalRounds: z.coerce.number(),
    totalBet: amount,
    totalBaseWin: amount,
    totalBonusWin: amount,
    configuredRtp: z.unknown().transform((v) => normalizeRtp(v)),
    baseRtp: pct,
    bonusRtp: pct,
    actualTotalRtp: pct,
  }),
  bonusGames: z
    .array(
      z.object({
        name: z.string(),
        triggers: z.coerce.number(),
        totalWin: amount,
        featureRtp: pct,
      }),
    )
    .default([]),
});

export type PlayerRtp = z.infer<typeof rtpSchema>;

export const playerRtpQuery = (playerId: number, gameId: number, from: string, to: string) =>
  queryOptions({
    queryKey: playersKeys.rtp(playerId, gameId, from, to),
    queryFn: ({ signal }) =>
      http(
        `/players/${playerId}/rtp-analytics`,
        { query: { gameId, startDate: from, endDate: to }, signal },
        rtpSchema,
      ),
  });

// ---- sessions ---------------------------------------------------------------------------------

/** The safe part of a session row. Hash, IP and wallet session state are never read. */
export const sessionSchema = z.object({
  PlayerId: z.coerce.number(),
  GameId: count,
  Currency: text,
  Channel: count,
  Status: count,
  BetAmount: z
    .union([z.string(), z.number()])
    .nullish()
    .transform((v) => (v === null || v === undefined ? null : String(v))),
  BetCount: count,
  NotifiedAsLoggedOut: flag.optional(),
  CreatedAt: timestamp,
  UpdatedAt: timestamp,
});

export type Session = z.infer<typeof sessionSchema>;

/** The player's current session, or null when there's none (404). */
export const playerSessionQuery = (playerId: number) =>
  queryOptions({
    queryKey: playersKeys.session(playerId),
    queryFn: async ({ signal }) => {
      try {
        return await http(`/sessions/${playerId}`, { signal }, sessionSchema);
      } catch (error) {
        if (isApiError(error) && error.code === 'not_found') return null;
        throw error;
      }
    },
  });

/** Sessions accept paging only; the server sorts newest first. */
export const sessionsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  size: z.coerce
    .number()
    .refine((n) => [10, 25, 50, 100].includes(n))
    .optional()
    .catch(undefined),
});

export type SessionsSearch = z.infer<typeof sessionsSearchSchema>;

export const sessionsListQuery = (search: SessionsSearch) => {
  const query = toPagedQuery(search);
  const { skip, take } = query;
  return queryOptions({
    queryKey: playersKeys.sessions(query),
    queryFn: ({ signal }) => http('/sessions', { query: { skip, take }, signal }, pagedSchema(sessionSchema)),
    placeholderData: keepPreviousData,
  });
};

/** Session status codes aren't documented; they're shown as they are. */
export const sessionStatusLabel = (status: number | null) => (status === null ? '—' : String(status));

// ---- dates ------------------------------------------------------------------------------------

const utcDay = (offset: number) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};

/** The analytics tabs' default window: the last 30 UTC days, today included. */
export const defaultRange = () => ({ from: utcDay(-29), to: utcDay(0) });
